import { useRef, useState } from 'react';
import { cx } from 'class-variance-authority';
import * as v from 'valibot';

import { Button, DataTable, Icon, Modal, Pagination, Radio, RadioGroup, Spinner, Text } from '@nvision/ui';

import { NoData } from '@/modules/errors';

import { useGetLanes } from '../../../hooks';
import { type LaneRow, type LaneSearchFormValues, type LanesParams, laneSearchParamsSchema } from '../../../models';
import { LaneSearchForm, type SearchFormRef } from '../lane-search-form';

import styles from './lane-search-modal.module.css';

const EMPTY_CELL = '—';

interface LaneSearchModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange?: (value: LaneRow) => void;
}

export const LaneSearchModal: React.FC<LaneSearchModalProps> = ({ isOpen = false, setIsOpen, onSelectionChange }) => {
  const [params, setParams] = useState<LanesParams>(v.getDefaults(laneSearchParamsSchema));
  const [selectedLane, setSelectedLane] = useState<LaneRow | null>(null);

  const { data: lanes, isFetching } = useGetLanes(params);

  const searchFormRef = useRef<SearchFormRef>({} as SearchFormRef);

  const reset = () => {
    setSelectedLane(null);
    setParams(v.getDefaults(laneSearchParamsSchema));
  };

  const close = () => {
    searchFormRef.current?.reset?.();
    setSelectedLane(null);
    setIsOpen(false);
  };

  const submit = (values: LaneSearchFormValues) => {
    setParams((prev) => ({
      ...prev,
      carrierKey: values.carrierKey,
      destinationCity: values.destinationCity,
      destinationCountry: values.destinationCountry?.id ?? '',
      destinationPortKey: values.destinationPortKey,
      destinationPostal: values.destinationPostal,
      destinationState: values.destinationState?.id,
      laneCode: values.laneCode,
      offset: 0,
      originCity: values.originCity,
      originCountry: values.originCountry?.id ?? '',
      originPortKey: values.originPortKey,
      originPostal: values.originPostal,
      originState: values.originState?.id,
    }));
  };

  const handleSubmitSelection = () => {
    if (selectedLane && onSelectionChange) {
      onSelectionChange(selectedLane);
      close();
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(value) => setIsOpen(value)} className={styles.modal}>
      <div className={styles.formWrapper}>
        <Modal.Header className={styles.header}>
          <Text size="xlg" weight="semibold">
            Lane Search
          </Text>
          <Icon name="x" onClick={close} cursor="pointer" />
        </Modal.Header>
        <Modal.Content className={styles.content}>
          <LaneSearchForm formRef={searchFormRef} handleSubmit={submit} handleReset={reset} />
          {isFetching ? (
            <div className={cx(styles.tableContainer, styles.loader)}>
              <Spinner size="lg" />
            </div>
          ) : lanes?.data.length ? (
            <div>
              <div className={styles.total}>
                {lanes.total === 1 ? (
                  <>
                    <Text weight="bold">{`${lanes.total} lane `}</Text>is found
                  </>
                ) : (
                  <>
                    <Text weight="bold">{`${lanes.total} lanes `}</Text>are found
                  </>
                )}
              </div>
              <div className={styles.tableContainer}>
                <DataTable<LaneRow>
                  columns={[
                    {
                      cell: (props) => {
                        const row = props.row.original;
                        const isSelected = selectedLane?.laneKey === row.laneKey;

                        return (
                          <RadioGroup
                            value={isSelected ? String(row.laneKey) : ''}
                            onChange={() => setSelectedLane(row)}
                            className={styles.radio}
                          >
                            <Radio value={String(row.laneKey)} className={styles.radio} />
                          </RadioGroup>
                        );
                      },
                      enableSorting: false,
                      header: '',
                      id: 'select',
                      maxSize: 0,
                    },
                    {
                      accessorKey: 'laneKey',
                      cell: (props) => props.row.original.laneKey || EMPTY_CELL,
                      enableSorting: false,
                      header: 'Lane Key',
                    },
                    {
                      accessorKey: 'laneCode',
                      cell: (props) => props.row.original.laneCode || EMPTY_CELL,
                      enableSorting: false,
                      header: 'Lane ID',
                    },
                    {
                      accessorKey: 'providerLaneID',
                      cell: (props) => props.row.original.providerLaneID || EMPTY_CELL,
                      enableSorting: false,
                      header: 'Provider Lane ID',
                    },
                    {
                      accessorKey: 'carrierKey',
                      cell: (props) => props.row.original.carrierKey ?? EMPTY_CELL,
                      enableSorting: false,
                      header: 'Transportation Provider',
                    },
                  ]}
                  data={lanes.data}
                  isLoading={isFetching}
                  page={params.offset / params.limit + 1}
                  limit={params.limit}
                  totalItems={lanes.total}
                />
              </div>

              <Pagination
                className={styles.pagination}
                totalItems={lanes.total}
                limit={params.limit}
                page={params.offset / params.limit + 1}
                onPageChange={(page: number) => {
                  setParams((prev) => ({ ...prev, offset: (page - 1) * prev.limit }));
                }}
                onOffsetChange={(offset: number) => setParams((prev) => ({ ...prev, offset }))}
                onLimitChange={(limit: number) => setParams((prev) => ({ ...prev, limit, offset: 0 }))}
              />
            </div>
          ) : (
            <NoData
              className={styles.noData}
              description="No data to display. Please use the search above to find lanes."
            />
          )}
        </Modal.Content>
        <Modal.Footer className={styles.footer}>
          <Button outline secondary onClick={close}>
            Cancel
          </Button>
          <Button isDisabled={!selectedLane} onClick={handleSubmitSelection}>
            Submit
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};
